'use client'
import { equals, head, not, pick, pipe } from 'ramda'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction
} from 'react'
import { type ClientsResp, type FeaturePlan } from 'shared/api/clients'
import { useChangePath, useQueryString } from 'shared/components/Router'
import { DEFAULT_CURRENCY_CODE, type CurrencyCodes } from 'shared/constants/currency'
import { DEFAULT_FEATURE_PLAN } from 'shared/constants/featurePlans'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'shared/context/AuthContext'
import { APP_ROUTES, CLIENT_ID_QUERY_PARAM } from 'shared/routes'
import { getFromStore, noop, removeToStore, saveToStore } from 'shared/utils'
import * as cookieStore from 'shared/cookieStore'
import { useQueryClients } from 'src/workspace/api'

interface BaseClient {
  id: number
  name: string
  url?: string
  avatarUrl?: string
  currency: CurrencyCodes
}
export interface Client extends BaseClient {
  featurePlan: FeaturePlan
}

interface IClientContext {
  currentClient: Client
  changeCurrentClient: (client: Partial<Client>) => Promise<void>
  clearCachedClient: () => void
  isLoading: boolean
  isValidClient: boolean
  isSuccess: boolean
  isRefetching: boolean
  error?: Error
}

export const NoClient = {
  name: '',
  url: '',
  id: 0,
  avatarUrl: '',
  currency: DEFAULT_CURRENCY_CODE,
  featurePlan: DEFAULT_FEATURE_PLAN
}

const defaultValue: IClientContext = {
  currentClient: NoClient,
  changeCurrentClient: async () => {
    noop()
  },
  clearCachedClient: noop,
  isValidClient: false,
  isLoading: true,
  isSuccess: false,
  isRefetching: false
}

interface Props {
  children: ReactNode
}

const ClientContext = createContext<IClientContext>(defaultValue)

export const getRootPath = (path: string): string => {
  const parts = path.split('/')
  const firstPart = parts.length > 1 ? parts[1] : ''
  return `/${firstPart}`
}

const getFirstAlphabiticalOrderedClient = (clients: Client[]): Client | undefined =>
  head(clients.sort((curr: Client, next: Client) => curr.name.localeCompare(next.name))) as Client

const getIndexOfClient = ({
  clients = [],
  currentClient
}: {
  clients: ClientsResp | undefined
  currentClient: Client
}): number => clients.findIndex((client) => client.id === currentClient.id)

export const saveClientToStore = (currentClient: BaseClient | undefined): void => {
  if (currentClient != null) {
    saveToStore('clients', JSON.stringify(currentClient))
    cookieStore.store('clientId', currentClient.id.toString())
  } else {
    removeToStore('clients')
    cookieStore.remove('clientId')
  }
}

export const getInitialCurrentClient = ({
  queryClientId,
  storedClientsJson
}: {
  queryClientId: string | string[] | undefined | null
  storedClientsJson: string | null
}): Client => {
  if (queryClientId != null) {
    return {
      ...NoClient,
      id: Number(queryClientId as string)
    }
  }
  if (storedClientsJson !== null) {
    return JSON.parse(storedClientsJson) as Client
  }
  return NoClient
}

const changeCurrentClient =
  (setCurrentClient: Dispatch<SetStateAction<Client>>) =>
  ({ client, currentClient }: { client: Partial<Client>; currentClient: Client }): Client => {
    const newClient = {
      ...(client as Client),
      ...(client?.name != null ? { name: client.name } : {})
    }

    setCurrentClient(newClient)
    saveClientToStore(pick(['id', 'name', 'url', 'avatarUrl', 'currency'], newClient))
    return newClient
  }

export const useCurrentClient = (): IClientContext => useContext<IClientContext>(ClientContext)

const INCORRECT_CLIENT_ID_IN_QUERYSTRINGERROR = new Error('Incorrect client id in query string')

export const ClientProvider = (props: Props): React.ReactElement => {
  const [error, setError] = useState<Error | undefined>(undefined)
  const queryClient = useQueryClient()
  const { isSuccess: isAuthSuccess } = useAuth()
  const { pathname, changePath } = useChangePath()
  const { pushQuery } = useQueryString()
  const {
    query: { clientId: queryClientId }
  } = useQueryString()

  const redirectToWorkspaceList = useCallback((): void => {
    void changePath(APP_ROUTES.workspace.listPage)
  }, [changePath])

  const getDefaultCurrentClient = (): Client =>
    getInitialCurrentClient({
      queryClientId,
      storedClientsJson: typeof window !== 'undefined' ? (getFromStore('clients') as string | null) : null
    })

  const [currentClient, setCurrentClient] = useState<Client>(getDefaultCurrentClient)

  // Shape check only (non-default id+name, no error) — not membership in `clients`.
  const hasValidClientIdAndNameInStore =
    currentClient.id !== defaultValue.currentClient.id &&
    currentClient.name !== defaultValue.currentClient.name &&
    error == null
  const isValidClient = hasValidClientIdAndNameInStore

  const canFetchAllClients = isAuthSuccess
  const {
    data: clients = [],
    isLoading: isLoadingAllClients,
    isSuccess: isClientsSuccess,
    isRefetching
  } = useQueryClients(canFetchAllClients)

  const clearCachedClient = useCallback(() => {
    saveClientToStore(undefined)
    setCurrentClient(defaultValue.currentClient)
  }, [])

  const changeClientAndQuery = useCallback(
    async ({ client, currentClient }: { client: Partial<Client>; currentClient: Client }): Promise<void> => {
      const params = new URLSearchParams({ [CLIENT_ID_QUERY_PARAM]: client.id?.toString() ?? '' })
      queryClient.clear()
      window.location.assign(`${getRootPath(pathname)}?${params.toString()}`)
    },
    [pathname, queryClient]
  )

  useEffect(() => {
    const hasInvalidClientAndNoQueryClientId =
      queryClientId == null && isClientsSuccess && not(hasValidClientIdAndNameInStore)

    void (async function changeClient() {
      if (hasInvalidClientAndNoQueryClientId) {
        redirectToWorkspaceList()
      }
    })()
  }, [
    clients,
    isClientsSuccess,
    hasValidClientIdAndNameInStore,
    currentClient,
    queryClientId,
    changeClientAndQuery,
    redirectToWorkspaceList
  ])

  useEffect(() => {
    // If the query string does not have a client id, and the current client is not valid, and there are clients, then change the current client to a new client
    void (async function changeClient() {
      const isNotActiveClient = pipe(() => ({ clients, currentClient }), getIndexOfClient, equals(-1))
      const possibleNewClient = getFirstAlphabiticalOrderedClient(clients as Client[])

      if (
        isClientsSuccess &&
        queryClientId == null &&
        hasValidClientIdAndNameInStore &&
        isNotActiveClient() &&
        clients.length > 0 &&
        possibleNewClient != null
      ) {
        await changeClientAndQuery({
          client: possibleNewClient,
          currentClient
        })
      } else if (isClientsSuccess && queryClientId == null && hasValidClientIdAndNameInStore) {
        const clientExists = clients.some((client) => client.id === currentClient.id)

        if (clientExists) {
          await pushQuery({
            [CLIENT_ID_QUERY_PARAM]: currentClient.id?.toString()
          })
        } else {
          clearCachedClient()
        }
      }
    })()
  }, [
    clients,
    isClientsSuccess,
    hasValidClientIdAndNameInStore,
    currentClient,
    queryClientId,
    changeClientAndQuery,
    pushQuery,
    clearCachedClient
  ])

  useEffect(() => {
    const hasQueryClientIdNotMatchingCurrentClientId =
      queryClientId != null && isClientsSuccess && not(hasValidClientIdAndNameInStore)

    if (hasQueryClientIdNotMatchingCurrentClientId) {
      const client = clients.find((client) => client.id.toString() === queryClientId.toString())
      client != null
        ? changeCurrentClient(setCurrentClient)({ client, currentClient })
        : setError(INCORRECT_CLIENT_ID_IN_QUERYSTRINGERROR)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClientId, clients, isClientsSuccess, currentClient])

  useEffect(() => {
    const clientWithFullDetails = clients.find((client) => client.id.toString() === currentClient.id.toString())

    const hasClientWithMissingDetails =
      clientWithFullDetails !== undefined && !equals(clientWithFullDetails, currentClient)

    if (hasClientWithMissingDetails) {
      changeCurrentClient(setCurrentClient)({ client: clientWithFullDetails, currentClient })
    }
  }, [clients, currentClient])

  const isSuccess = isClientsSuccess
  const isLoading = isLoadingAllClients

  return (
    <ClientContext.Provider
      value={{
        changeCurrentClient: async (client): Promise<void> => {
          await changeClientAndQuery({ client, currentClient })
        },
        clearCachedClient,
        isSuccess,
        isLoading,
        currentClient,
        isValidClient,
        isRefetching,
        error
      }}
    >
      {props.children}
    </ClientContext.Provider>
  )
}
